import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  clamp01,
  splitFromClientX,
  stepWipe,
  WIPE_KEYBOARD_STEP,
  WIPE_PERIOD_MS,
  WIPE_RESUME_MS,
  WipeDirection,
} from './wipe-motion';
import './poster-wipe.css';

const prefersReducedMotion = () => (
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
);

type PosterWipeProps = {
  sketchUrl: string;
  posterUrl: string;
};

export const PosterWipe = ({ sketchUrl, posterUrl }: PosterWipeProps) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const offscreenRef = useRef(false);
  const splitRef = useRef(0);
  const directionRef = useRef<WipeDirection>(1);
  const resumeTimerRef = useRef<number | undefined>(undefined);
  const reducedMotion = prefersReducedMotion();
  const [split, setSplit] = useState(reducedMotion ? 0.5 : 0);
  const [dragging, setDragging] = useState(false);

  splitRef.current = split;

  const pauseAutoplay = useCallback(() => {
    pausedRef.current = true;
    if (resumeTimerRef.current !== undefined) {
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = undefined;
    }
  }, []);

  const scheduleResume = useCallback(() => {
    if (prefersReducedMotion()) {
      return;
    }
    if (resumeTimerRef.current !== undefined) {
      window.clearTimeout(resumeTimerRef.current);
    }
    resumeTimerRef.current = window.setTimeout(() => {
      pausedRef.current = false;
      resumeTimerRef.current = undefined;
    }, WIPE_RESUME_MS);
  }, []);

  const applySplit = useCallback((next: number, dragDirection?: WipeDirection) => {
    const clamped = clamp01(next);
    if (dragDirection) {
      directionRef.current = dragDirection;
    }
    splitRef.current = clamped;
    setSplit(clamped);
  }, []);

  const setSplitFromClientX = useCallback((clientX: number) => {
    const frame = frameRef.current;
    if (!frame) {
      return;
    }
    const rect = frame.getBoundingClientRect();
    const next = splitFromClientX(clientX, rect.left, rect.width);
    const dragDirection: WipeDirection | undefined = next > splitRef.current
      ? 1
      : next < splitRef.current
        ? -1
        : undefined;
    applySplit(next, dragDirection);
  }, [applySplit]);

  useEffect(() => {
    if (reducedMotion) {
      return;
    }
    let frameId = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(now - last, 48);
      last = now;
      if (!pausedRef.current && !offscreenRef.current) {
        const next = stepWipe(
          splitRef.current,
          directionRef.current,
          dt,
          WIPE_PERIOD_MS
        );
        directionRef.current = next.direction;
        splitRef.current = next.split;
        setSplit(next.split);
      }
      frameId = window.requestAnimationFrame(tick);
    };
    frameId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frameId);
  }, [reducedMotion]);

  useEffect(() => {
    const node = frameRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      return;
    }
    const observer = new IntersectionObserver(entries => {
      offscreenRef.current = !entries[0]?.isIntersecting;
    }, { threshold: 0.15 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      if (resumeTimerRef.current !== undefined) {
        window.clearTimeout(resumeTimerRef.current);
      }
    };
  }, []);

  const onPointerDown = (evt: React.PointerEvent<HTMLElement>) => {
    evt.preventDefault();
    evt.currentTarget.setPointerCapture(evt.pointerId);
    setDragging(true);
    pauseAutoplay();
    setSplitFromClientX(evt.clientX);
  };

  const onPointerMove = (evt: React.PointerEvent<HTMLElement>) => {
    if (!evt.currentTarget.hasPointerCapture(evt.pointerId)) {
      return;
    }
    setSplitFromClientX(evt.clientX);
  };

  const onPointerUp = (evt: React.PointerEvent<HTMLElement>) => {
    if (evt.currentTarget.hasPointerCapture(evt.pointerId)) {
      evt.currentTarget.releasePointerCapture(evt.pointerId);
    }
    setDragging(false);
    scheduleResume();
  };

  const onKeyDown = (evt: React.KeyboardEvent<HTMLElement>) => {
    if (evt.key !== 'ArrowLeft' && evt.key !== 'ArrowRight') {
      return;
    }
    evt.preventDefault();
    pauseAutoplay();
    const delta = evt.key === 'ArrowRight' ? WIPE_KEYBOARD_STEP : -WIPE_KEYBOARD_STEP;
    directionRef.current = delta > 0 ? 1 : -1;
    applySplit(splitRef.current + delta);
    scheduleResume();
  };

  const percent = split * 100;

  return (
    <div className={`posterWipe${dragging ? ' dragging' : ''}`}>
      <div className='posterWipeFrame' ref={frameRef}>
        <img
          className='posterWipeSketch'
          src={sketchUrl}
          alt='Original sketch'
          draggable={false}
        />
        <img
          className='posterWipePoster'
          src={posterUrl}
          alt='Generated poster'
          draggable={false}
          style={{ clipPath: `inset(0 ${100 - percent}% 0 0)` }}
        />
        <div
          className='posterWipeRule'
          style={{ left: `${percent}%` }}
          aria-hidden='true'
        />
        <div
          className='posterWipeHit'
          style={{ left: `${percent}%` }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      </div>
      <div
        className='posterWipeHandle'
        style={{ left: `${percent}%` }}
        role='slider'
        tabIndex={0}
        aria-label='Compare sketch and poster'
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(percent)}
        aria-valuetext={`${Math.round(percent)} percent poster`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
      />
    </div>
  );
};
